import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase.js';
import { api } from '../lib/api.js';
import { GlowCard } from '../components/GlowCard.js';

interface NotificationPrefs {
  pipelineComplete: boolean;
  contentReady: boolean;
  publishFailed: boolean;
}

function SectionCard({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <GlowCard className="rounded-xl border border-white/10 bg-pix-deep px-6 py-5">
      <div className="mb-4 border-b border-white/5 pb-4">
        <h2 className="text-base font-semibold text-white">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-white/50">{description}</p>}
      </div>
      {children}
    </GlowCard>
  );
}

function Toggle({ checked, onChange, label, description }: {
  checked: boolean;
  onChange: (val: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div>
        <p className="text-sm font-medium text-white">{label}</p>
        {description && <p className="text-xs text-white/50">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={[
          'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent',
          'transition-colors duration-200',
          checked ? 'bg-pix-pink' : 'bg-gray-200',
        ].join(' ')}
      >
        <span
          className={[
            'pointer-events-none inline-block h-4 w-4 rounded-full bg-pix-deep shadow',
            'transition-transform duration-200',
            checked ? 'translate-x-4' : 'translate-x-0',
          ].join(' ')}
        />
      </button>
    </div>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();

  // Account info state
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [displayNameInput, setDisplayNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  // Notification prefs state
  const [notifPrefs, setNotifPrefs] = useState<NotificationPrefs>({
    pipelineComplete: true,
    contentReady: true,
    publishFailed: true,
  });
  const [savingNotifs, setSavingNotifs] = useState(false);
  const [notifError, setNotifError] = useState<string | null>(null);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    async function loadUser() {
      const { data } = await supabase.auth.getUser();
      if (data.user) {
        setEmail(data.user.email ?? '');
        const fullName = (data.user.user_metadata?.['full_name'] as string | undefined) ?? '';
        setDisplayName(fullName);
        setDisplayNameInput(fullName);
        const prefs = data.user.user_metadata?.['notification_prefs'] as Partial<NotificationPrefs> | undefined;
        if (prefs) {
          setNotifPrefs({
            pipelineComplete: prefs.pipelineComplete ?? true,
            contentReady: prefs.contentReady ?? true,
            publishFailed: prefs.publishFailed ?? true,
          });
        }
      }
    }
    loadUser();
  }, []);

  async function handleSaveName(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = displayNameInput.trim();
    if (trimmed === displayName) return;
    setSavingName(true);
    setNameError(null);
    setNameSaved(false);
    const { error } = await supabase.auth.updateUser({ data: { full_name: trimmed } });
    setSavingName(false);
    if (error) {
      setNameError('Failed to update display name. Please try again.');
    } else {
      setDisplayName(trimmed);
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 3000);
    }
  }

  async function handleNotifToggle(key: keyof NotificationPrefs, val: boolean) {
    const updated = { ...notifPrefs, [key]: val };
    setNotifPrefs(updated);
    setSavingNotifs(true);
    setNotifError(null);
    const { error } = await supabase.auth.updateUser({ data: { notification_prefs: updated } });
    setSavingNotifs(false);
    if (error) {
      setNotifError('Failed to save notification preferences.');
      // Revert on error
      setNotifPrefs(notifPrefs);
    }
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    setDeleteError(null);
    const result = await api.delete<null>('/api/account');
    if (result.error) {
      setDeleting(false);
      setDeleteError('Failed to delete account. Please try again or contact support.');
      return;
    }
    await supabase.auth.signOut();
    navigate('/signup');
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white">Settings</h1>
      <p className="mt-1 text-sm text-white/50">Manage your account and notification preferences.</p>

      <div className="mt-6 space-y-6">
        {/* Account info */}
        <SectionCard title="Account info" description="Update your display name.">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/70">Email</label>
              <p className="mt-1 rounded-lg border border-white/10 bg-pix-void px-3 py-2 text-sm text-white/50 select-all">
                {email || '\u00A0'}
              </p>
            </div>
            <form onSubmit={handleSaveName} className="space-y-3">
              <div>
                <label htmlFor="display-name" className="block text-sm font-medium text-white/70">
                  Display name
                </label>
                <input
                  id="display-name"
                  type="text"
                  value={displayNameInput}
                  onChange={(e) => setDisplayNameInput(e.target.value)}
                  placeholder="Your name"
                  className="mt-1 block w-full rounded-lg border border-white/15 px-3 py-2 text-sm text-white placeholder-gray-400 focus:border-pix-pink/50 focus:outline-none focus:ring-1 focus:ring-pix-pink/50"
                />
              </div>
              {nameError && (
                <p className="text-sm text-red-400">{nameError}</p>
              )}
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={savingName || displayNameInput.trim() === displayName}
                  className="rounded-lg bg-pix-pink px-4 py-2 text-sm font-semibold text-white transition-opacity duration-150 hover:bg-pix-pink-dark disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingName ? 'Saving...' : 'Save'}
                </button>
                {nameSaved && (
                  <span className="text-sm text-green-400">Saved!</span>
                )}
              </div>
            </form>
          </div>
        </SectionCard>

        {/* Notification preferences */}
        <SectionCard title="Notification preferences" description="Choose which emails you receive from Cascade.">
          <div className="divide-y divide-white/5">
            <Toggle
              label="Pipeline run complete"
              description="Notified when a pipeline run finishes."
              checked={notifPrefs.pipelineComplete}
              onChange={(val) => handleNotifToggle('pipelineComplete', val)}
            />
            <Toggle
              label="Content ready for review"
              description="Notified when AI-generated content is ready."
              checked={notifPrefs.contentReady}
              onChange={(val) => handleNotifToggle('contentReady', val)}
            />
            <Toggle
              label="Publish failed"
              description="Notified when a publish attempt fails."
              checked={notifPrefs.publishFailed}
              onChange={(val) => handleNotifToggle('publishFailed', val)}
            />
          </div>
          {savingNotifs && (
            <p className="mt-3 text-xs text-white/40">Saving preferences...</p>
          )}
          {notifError && (
            <p className="mt-3 text-sm text-red-400">{notifError}</p>
          )}
        </SectionCard>

        {/* Danger zone */}
        <SectionCard title="Danger zone">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-white">Delete account</p>
              <p className="mt-0.5 text-sm text-white/50">
                Permanently delete your account and all associated pipelines and data. This cannot be undone.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="shrink-0 rounded-lg border border-red-500/30 bg-pix-deep px-4 py-2 text-sm font-semibold text-red-400 transition-colors duration-150 hover:bg-red-500/10"
            >
              Delete account
            </button>
          </div>
        </SectionCard>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-md rounded-xl bg-pix-deep p-6 shadow-xl">
            <h3 className="text-base font-semibold text-white">Delete your account?</h3>
            <p className="mt-2 text-sm text-white/50">
              This will permanently delete all your pipelines, content, and account data. You cannot recover this data
              after deletion.
            </p>
            {deleteError && (
              <p className="mt-3 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-400">{deleteError}</p>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowDeleteModal(false); setDeleteError(null); }}
                disabled={deleting}
                className="rounded-lg border border-white/15 bg-pix-deep px-4 py-2 text-sm font-semibold text-white/70 transition-colors duration-150 hover:bg-pix-void disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-opacity duration-150 hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Yes, delete everything'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
